// Location.jsx
import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import LocationForm from "./LocationForm";

const LOCATION_TYPE_KEYS = {
  "Résidence principale": "locationManager.types.primaryResidence",
  "Résidence secondaire": "locationManager.types.secondaryResidence",
  "Bureau": "locationManager.types.office",
  "Réserve personnelle": "locationManager.types.personalReserve",
  "Garde-meuble": "locationManager.types.selfStorage",
  "Entrepôt d'art spécialisé": "locationManager.types.artWarehouse",
  "Galerie / atelier / dépôt": "locationManager.types.professionalDeposit",
  "Musée / institution": "locationManager.types.museum",
  "Prêt": "locationManager.types.loan",
  "Exposition": "locationManager.types.exhibition",
  "Transport / transit": "locationManager.types.transit",
  "Entrepôt sous douane / port franc": "locationManager.types.freeport",
  "Stockage": "locationManager.types.storage", // legacy générique
  "Autre": "locationManager.types.other",
};

const SECURITY_KEYS = {
  "Alarme intrusion": "locationManager.security.intrusionAlarm",
  "Vidéosurveillance": "locationManager.security.videoSurveillance",
  "Accès restreint": "locationManager.security.restrictedAccess",
  "Assurance spécifique": "locationManager.security.specificInsurance",
};

// Statuts « Lieux & conservation » adoucis (cf. RECOMMANDATIONS_..._LIEUX_CONSERVATION §3).
// On n'affiche plus un verdict brut « risque élevé » mais un statut gradué et rassurant,
// palette sauge / ambre / terracotta, bordure + texte (pas seulement une couleur).
const STORAGE_STATUS = {
  "faible": { key: "locationManager.status.suitable", fg: "#5b7052", bg: "#eef2ea", border: "#d6e0cf" },
  "moyen":  { key: "locationManager.status.improve",  fg: "#8a6d2f", bg: "#faf3e2", border: "#ecdcb8" },
  "élevé":  { key: "locationManager.status.monitor",  fg: "#a85643", bg: "#f7ece7", border: "#e6cdc2" },
};

function PhotoGallery({ location, onPhotosUpdated }) {
  const { t } = useTranslation();
  const [lightbox, setLightbox] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const photos = Array.isArray(location.storage_photos) && location.storage_photos.length > 0
    ? location.storage_photos
    : (typeof location.storage_photo_url === "string" && location.storage_photo_url ? [location.storage_photo_url] : []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const ext = file.name.split(".").pop();
        const path = `locations/photos/photo_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from("artworks-images").upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(path);
        newUrls.push(pub.publicUrl);
      }
      const updatedPhotos = [...photos, ...newUrls];
      const { error: updateError } = await supabase
        .from("locations")
        .update({
          storage_photos: updatedPhotos,
          storage_photo_url: updatedPhotos[0] || null,
        })
        .eq("id", location.id);
      if (updateError) throw updateError;
      if (onPhotosUpdated) onPhotosUpdated(updatedPhotos);
    } catch (err) {
      alert(t("locationManager.notifications.uploadError", { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="mt-2">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <strong className="text-sm">{t("locationManager.photos.title")}</strong>
          <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" style={{ display: "none" }} onChange={handleUpload} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              fontSize: 11, fontFamily: "system-ui", fontWeight: 600,
              color: uploading ? "#aaa" : "#c9a961",
              background: "none", border: "none", cursor: uploading ? "not-allowed" : "pointer",
              padding: 0,
            }}
          >
            {uploading
              ? t("locationManager.photos.uploadingShort")
              : photos.length > 0
                ? t("locationManager.photos.add")
                : t("locationManager.photos.addPhotos")}
          </button>
        </div>
        {photos.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {photos.map((url) => (
              <div key={url} onClick={() => setLightbox(url)}
                style={{ aspectRatio: "1", borderRadius: 4, overflow: "hidden", cursor: "pointer", background: "#f0ece5" }}>
                <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            ))}
          </div>
        )}
        {photos.length === 0 && (
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              width: "100%", padding: "10px", marginTop: 4,
              border: "2px dashed #e0ddd8", borderRadius: 6,
              background: "#fafaf8", cursor: "pointer",
              fontSize: 12, color: "#aaa", fontFamily: "system-ui",
            }}
          >
            {uploading ? t("locationManager.photos.uploading") : t("locationManager.photos.addSpacePhotos")}
          </button>
        )}
      </div>
      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out" }}>
          <img src={lightbox} alt="" style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 4 }} />
        </div>
      )}
    </>
  );
}

export default function LocationComponent() {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [, setSelectedLocation] = useState(null);
  const [editModeByLocationId, setEditModeByLocationId] = useState({});
  const [artworksByLocation, setArtworksByLocation] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState("");
  const [selectedArtworks, setSelectedArtworks] = useState({});
  const [transferConfirm, setTransferConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetchLocations = async () => {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) return;

      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("client_id", user.id);

      if (!error) {
        setLocations(data);
        data.forEach(loc => fetchArtworks(loc.id));
      }
    };

    fetchLocations();
  }, []);

  const fetchArtworks = async (locationId) => {
    const { data, error } = await supabase
      .from("artworks")
      .select("*")
      .eq("location_id", locationId);

    if (!error) {
      setArtworksByLocation(prev => ({ ...prev, [locationId]: data }));
    }
  };

  const toggleEditMode = (locationId, enabled) => {
    setEditModeByLocationId((prev) => ({ ...prev, [locationId]: enabled }));
  };

  const toggleArtworkSelection = (locationId, artworkId) => {
    setSelectedArtworks(prev => {
      const current = prev[locationId] || [];
      return {
        ...prev,
        [locationId]: current.includes(artworkId)
          ? current.filter(id => id !== artworkId)
          : [...current, artworkId],
      };
    });
  };

  const confirmTransferWithModal = (sourceLocationId) => {
    const artworkIds = selectedArtworks[sourceLocationId] || [];
    if (!transferTargetId || artworkIds.length === 0) {
      toast.error(t("locationManager.notifications.selectTransfer"));
      return;
    }

    const artworks = (artworksByLocation[sourceLocationId] || [])
      .filter(a => artworkIds.includes(a.id));

    setTransferConfirm({ sourceLocationId, artworkIds, artworks });
  };

  const executeTransfer = async () => {
    const { sourceLocationId, artworkIds } = transferConfirm;

    const { error } = await supabase.rpc("transfer_artworks", {
      artwork_ids: artworkIds,
      new_location_id: transferTargetId,
    });

    if (!error) {
      toast.success(t("locationManager.notifications.transferSuccess"));
      fetchArtworks(sourceLocationId);
      fetchArtworks(transferTargetId);
      setSelectedArtworks(prev => ({ ...prev, [sourceLocationId]: [] }));
      setTimeout(() => setTransferConfirm(null), 1200);
    } else {
      toast.error(t("locationManager.notifications.transferError"));
      setTransferConfirm(null);
    }
  };

  const handleUpdateLocation = async (id, updatedData) => {
    const { error } = await supabase
      .from("locations")
      .update(updatedData)
      .eq("id", id);

    if (!error) {
      toast.success(t("locationManager.notifications.updateSuccess"));
      toggleEditMode(id, false);
      setLocations((prev) =>
        prev.map((loc) => (loc.id === id ? { ...loc, ...updatedData } : loc))
      );
    } else {
      toast.error(t("locationManager.notifications.updateError"));
    }
  };

  const handleCreateLocation = async (newData) => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) return;

    const { data, error } = await supabase
      .from("locations")
      .insert([{ ...newData, client_id: user.id }])
      .select();

    if (!error) {
      toast.success(t("locationManager.notifications.createSuccess"));
      setLocations((prev) => [...prev, data[0]]);
      setShowCreateDialog(false);
    } else {
      toast.error(t("locationManager.notifications.createError"));
    }
  };

  const handleDeleteLocation = async (location) => {
    const artworks = artworksByLocation[location.id] || [];
    if (artworks.length > 0) {
      toast.error(t("locationManager.notifications.deleteBlocked", { count: artworks.length }));
      setDeleteConfirm(null);
      return;
    }
    const { error } = await supabase
      .from("locations")
      .delete()
      .eq("id", location.id);
    if (!error) {
      toast.success(t("locationManager.notifications.deleteSuccess"));
      setLocations((prev) => prev.filter((l) => l.id !== location.id));
    } else {
      toast.error(t("locationManager.notifications.deleteError"));
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <div className="flex justify-between items-start gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-light text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
            {t("locationManager.microcopy.headerTitle")}
          </h1>
          <p className="mt-2 text-sm md:text-base text-gray-500 leading-relaxed max-w-xl">
            {t("locationManager.microcopy.headerSub")}
          </p>
          <p className="mt-2 text-xs text-gray-400 leading-relaxed max-w-xl">
            <span className="text-[#a9843f] font-medium">{t("locationManager.microcopy.privacyTitle")}</span>{" "}
            {t("locationManager.microcopy.privacyText")}
          </p>
        </div>
        <button
          className="shrink-0 bg-black text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-900 transition-colors"
          onClick={() => setShowCreateDialog(prev => !prev)}
        >
          {showCreateDialog ? t("locationManager.actions.cancel") : t("locationManager.actions.newLocation")}
        </button>
      </div>

      {showCreateDialog && (
        <div className="mb-6 border p-4 rounded shadow-sm bg-gray-50">
          <h2 className="text-xl font-bold mb-4">{t("locationManager.actions.newLocation")}</h2>
          <LocationForm
            onCancel={() => setShowCreateDialog(false)}
            onSubmit={handleCreateLocation}
          />
        </div>
      )}

      {/* Réassurance « carnet de soin » (microcopy §12) — à l'onboarding, quand aucun lieu. */}
      {locations.length === 0 && !showCreateDialog && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {["home", "unknown", "pro"].map((k) => (
            <div key={k} className="rounded-2xl border border-[#e8e3d8] p-5" style={{ background: "#fffdf7" }}>
              <p className="text-sm font-semibold text-gray-900 mb-1">{t(`locationManager.microcopy.${k}Title`)}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{t(`locationManager.microcopy.${k}Text`)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-6">
        {locations.map((location) => (
          <div key={location.id} className="border p-4 rounded shadow-sm">
            {editModeByLocationId[location.id] ? (
              <LocationForm
                defaultValues={location}
                onCancel={() => toggleEditMode(location.id, false)}
                onSubmit={(data) => handleUpdateLocation(location.id, data)}
              />
            ) : (
              <>
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-semibold">{location.name}</h2>
                    <p className="text-sm text-gray-600">{LOCATION_TYPE_KEYS[location.type] ? t(LOCATION_TYPE_KEYS[location.type]) : location.type}</p>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {location.storage_risk && STORAGE_STATUS[location.storage_risk] && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontSize: 11.5, fontFamily: "system-ui", fontWeight: 600,
                        padding: "3px 10px", borderRadius: 999,
                        color: STORAGE_STATUS[location.storage_risk].fg,
                        background: STORAGE_STATUS[location.storage_risk].bg,
                        border: `1px solid ${STORAGE_STATUS[location.storage_risk].border}`,
                      }}>
                        <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, background: STORAGE_STATUS[location.storage_risk].fg }} />
                        {t(STORAGE_STATUS[location.storage_risk].key)}
                      </span>
                    )}
                    <button
                      className="text-[#a9843f] hover:underline text-sm font-medium"
                      onClick={() => {
                        setSelectedLocation(location);
                        toggleEditMode(location.id, true);
                      }}
                    >
                      {t("locationManager.actions.edit")}
                    </button>
                    <button
                      style={{ fontSize: 12, color: "#dc2626", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      onClick={() => setDeleteConfirm(location)}
                    >
                      {t("locationManager.actions.delete")}
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-sm text-gray-700 space-y-1">
                  <p><strong>{t("locationManager.fields.address")}:</strong> {location.address}</p>
                  <p><strong>{t("locationManager.fields.notes")}:</strong> {location.notes}</p>
                  {location.exposition_status && <p><strong>{t("locationManager.fields.status")}:</strong> {location.exposition_status}</p>}
                  {location.temperature && <p><strong>{t("locationManager.conditions.temperature")}:</strong> {location.temperature} °C</p>}
                  {location.humidity && <p><strong>{t("locationManager.conditions.humidity")}:</strong> {location.humidity} %</p>}
                  {location.light && <p><strong>{t("locationManager.conditions.light")}:</strong> {location.light}</p>}
                  {location.ventilation && <p><strong>{t("locationManager.conditions.ventilation")}:</strong> {location.ventilation}</p>}
                  {location.security_measures?.length > 0 && (
                    <div>
                      <strong>{t("locationManager.form.sections.security")}:</strong>
                      <ul className="list-disc list-inside">
                        {location.security_measures.map((s, i) => (
                          <li key={i}>{SECURITY_KEYS[s] ? t(SECURITY_KEYS[s]) : s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <PhotoGallery
                  location={location}
                  onPhotosUpdated={(newPhotos) =>
                    setLocations((prev) =>
                      prev.map((l) =>
                        l.id === location.id
                          ? { ...l, storage_photos: newPhotos, storage_photo_url: newPhotos[0] || null }
                          : l
                      )
                    )
                  }
                />
                {artworksByLocation[location.id]?.length > 0 && (
                  <div className="mt-4">
                    <h3 className="font-semibold">{t("locationManager.artworks.atLocation")}</h3>
                    <ul className="text-sm mt-2">
                      {artworksByLocation[location.id].map((artwork) => (
                        <li key={artwork.id} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={Array.isArray(selectedArtworks[location.id]) && selectedArtworks[location.id].includes(artwork.id)}
                            onChange={() => toggleArtworkSelection(location.id, artwork.id)}
                          />
                          <span>{artwork.title} – {artwork.artist}{artwork.inventory_number && (
                              <span style={{ color: "#888", fontSize: "11px", marginLeft: "8px", fontFamily: "Georgia, serif" }}>
                                [{artwork.inventory_number}]
                              </span>
                            )}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex space-x-2 items-center">
                      <select
                        className="border p-2 text-sm"
                        value={transferTargetId}
                        onChange={(e) => setTransferTargetId(e.target.value)}
                      >
                        <option value="">{t("locationManager.transfer.selectDestination")}</option>
                        {locations.filter(loc => loc.id !== location.id).map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => confirmTransferWithModal(location.id)}
                        className="bg-black text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-900 transition-colors"
                      >
                        {t("locationManager.actions.transfer")}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {transferConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h2 className="text-lg font-bold mb-4">{t("locationManager.transfer.confirmTitle")}</h2>
            <p className="mb-2">{t("locationManager.transfer.destination")}: <strong>{locations.find(l => l.id === transferTargetId)?.name}</strong></p>
            <ul className="list-disc list-inside text-sm mb-4">
              {transferConfirm.artworks.map((a) => (
                <li key={a.id}>{a.title || t("locationManager.fallback.untitled")} ({a.artist || t("locationManager.fallback.unknownArtist")})</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setTransferConfirm(null)}
                className="px-4 py-2 border border-[#e8e3d8] rounded-xl hover:bg-gray-50 transition-colors"
              >
                {t("locationManager.actions.cancel")}
              </button>
              <button
                onClick={executeTransfer}
                className="px-4 py-2 bg-black text-white rounded-xl hover:bg-gray-900 transition-colors"
              >
                {t("locationManager.actions.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-xl w-full max-w-sm">
            <h2 className="text-lg font-bold mb-3">{t("locationManager.delete.confirmTitle")}</h2>
            <p className="text-sm text-gray-600 mb-1">
              <strong>{deleteConfirm.name}</strong>
            </p>
            {(artworksByLocation[deleteConfirm.id] || []).length > 0 ? (
              <p className="text-sm text-red-600 mb-4">
                ⚠ {t("locationManager.delete.containsArtworks", { count: artworksByLocation[deleteConfirm.id].length })}
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-4">{t("locationManager.delete.irreversible")}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border rounded text-sm"
              >
                {t("locationManager.actions.cancel")}
              </button>
              <button
                onClick={() => handleDeleteLocation(deleteConfirm)}
                disabled={(artworksByLocation[deleteConfirm.id] || []).length > 0}
                style={{
                  padding: "8px 16px", background: (artworksByLocation[deleteConfirm.id] || []).length > 0 ? "#ccc" : "#dc2626",
                  color: "#fff", border: "none", borderRadius: 4,
                  fontSize: 13, fontWeight: 600, cursor: (artworksByLocation[deleteConfirm.id] || []).length > 0 ? "not-allowed" : "pointer",
                }}
              >
                {t("locationManager.actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const Location = LocationComponent;